import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { X } from "lucide-react";

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/** Unwrap Dynamo AttributeValue wrappers when present. */
function normalizeValue(value) {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(normalizeValue);
  if (!isPlainObject(value)) return value;

  const keys = Object.keys(value);
  if (keys.length === 1) {
    if (value.S != null) return value.S;
    if (value.N != null) {
      const n = Number(value.N);
      return Number.isFinite(n) ? n : value.N;
    }
    if (value.BOOL != null) return value.BOOL;
    if (value.NULL === true) return null;
    if (Array.isArray(value.L)) return value.L.map(normalizeValue);
    if (isPlainObject(value.M)) return normalizeValue(value.M);
  }

  const out = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = normalizeValue(v);
  }
  return out;
}

function isDrillable(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (isPlainObject(value)) return Object.keys(value).length > 0;
  return false;
}

function formatPrimitive(value) {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value === "" ? '""' : value;
  return String(value);
}

function summarize(value) {
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (isPlainObject(value)) return `Object(${Object.keys(value).length})`;
  return formatPrimitive(value);
}

function entriesFor(value) {
  if (Array.isArray(value)) {
    return value.map((item, index) => [String(index), item]);
  }
  if (isPlainObject(value)) {
    return Object.keys(value)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => [key, value[key]]);
  }
  return [];
}

/**
 * Key/value explorer for nested objects (routes, orgs, etc.).
 * Click nested objects/arrays to drill in; use breadcrumb to go back.
 */
const RouteDataModal = ({ open, setOpen, data, route, title, rootLabel = "root" }) => {
  const source = data ?? route ?? {};
  const root = useMemo(() => normalizeValue(source), [source]);
  const [stack, setStack] = useState([{ label: rootLabel, value: root }]);

  useEffect(() => {
    if (!open) return;
    setStack([{ label: rootLabel, value: normalizeValue(source) }]);
  }, [open, source, rootLabel]);

  const current = stack[stack.length - 1];
  const rows = entriesFor(current?.value);

  const drillIn = (key, value) => {
    if (!isDrillable(value)) return;
    setStack((prev) => [...prev, { label: key, value }]);
  };

  const goTo = (index) => {
    setStack((prev) => prev.slice(0, index + 1));
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="md"
      BackdropProps={{ style: { backgroundColor: "rgb(144,150,160,0.8)" } }}
      PaperProps={{ sx: { borderRadius: "12px", maxHeight: "85vh" } }}
    >
      <div className="px-6 py-5 flex flex-col min-h-0">
        <div className="relative mb-3 shrink-0">
          <div className="lg:text-[18px] text-[16px] font-semibold pr-8">
            {title || "Route details"}
          </div>
          <button
            type="button"
            className="absolute top-0 right-0 p-0.5"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <X className="w-[18px] text-gray-400" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1 text-[12px] text-gray-500 mb-3 shrink-0">
          {stack.map((frame, index) => {
            const isLast = index === stack.length - 1;
            return (
              <React.Fragment key={`${frame.label}-${index}`}>
                {index > 0 && (
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                )}
                {isLast ? (
                  <span className="font-semibold text-gray-800">
                    {frame.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => goTo(index)}
                    className="text-[#605BFF] hover:underline"
                  >
                    {frame.label}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="border border-gray-200 rounded-[10px] overflow-hidden overflow-y-auto max-h-[60vh]">
          {rows.length === 0 ? (
            <div className="px-4 py-6 text-[13px] text-gray-400 text-center">
              Empty
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-400 font-semibold w-[36%]">
                    Key
                  </th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([key, value]) => {
                  const nested = isDrillable(value);
                  return (
                    <tr
                      key={key}
                      className={`border-b border-gray-100 last:border-0 ${
                        nested ? "cursor-pointer hover:bg-[#F8FAFC]" : ""
                      }`}
                      onClick={() => nested && drillIn(key, value)}
                    >
                      <td className="px-3 py-2 align-top text-[13px] font-medium text-gray-700 break-all">
                        {key}
                      </td>
                      <td className="px-3 py-2 align-top text-[13px] text-gray-800 break-all">
                        {nested ? (
                          <span className="inline-flex items-center gap-1 text-[#605BFF]">
                            {summarize(value)}
                            <ChevronRight className="w-3.5 h-3.5" />
                          </span>
                        ) : (
                          <span className="whitespace-pre-wrap">
                            {formatPrimitive(value)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default RouteDataModal;
